'use client';

import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { VariantProps, cva } from 'class-variance-authority';
import classNames from 'classnames';
import * as React from 'react';
import { createContext, useContext, useMemo } from 'react';

import { cn } from '@/lib/css-utils';

import { buttonTextVariants } from './button';

const radioGroupVariants = cva(
    'inline-flex border border-black rounded-lg shrink p-0 overflow-hidden focus-within:outline! focus-within:outline-neutral-500! focus-within:outline-1!',
    {
        variants: {
            direction: {
                horizontal: 'gap-2 p-1',
                vertical: 'flex-col',
            },
            hasError: {
                true: 'border-token-stroke-input-error',
                false: '',
            },
            bordered: {
                true: 'outline! outline-neutral-500! focus-within:outline-neutral-400! outline-1!',
                false: '',
            },
            block: {
                true: 'w-full',
                false: '',
            },
        },
        defaultVariants: {
            direction: 'horizontal',
            hasError: false,
            block: false,
        },
    }
);

const radioItemVariants = cva(
    classNames(
        'flex gap-2 items-center justify-start',
        'text-center text-neutral-300 data-[state=checked]:text-neutral-200',
        'data-[state=checked]:bg-neutral-600 hover:bg-token-fill-button-secondary-pressed focus:bg-token-fill-button-secondary-hover',
        'disabled:text-token-text-dark-disabled disabled:hover:bg-transparent disabled:cursor-not-allowed',
        'text-xs leading-4.5 font-roboto focus:outline-none'
    ),
    {
        variants: {
            direction: {
                horizontal:
                    'rounded-lg justify-center border border-neutral-950 data-[state=checked]:border-neutral-700',
                vertical: 'border-t first:border-transparent',
            },
            block: {
                true: 'w-full',
                false: '',
            },

            size: {
                xs: 'px-4 py-[2.5px]',
                sm: 'px-4 py-[7px]',
                md: 'px-4 py-[11px]',
                lg: 'px-4 py-[15px]',
            },
        },
        defaultVariants: {
            direction: 'horizontal',
            size: 'sm',
            block: false,
        },
    }
);

const RadioButtonGroup = React.forwardRef<
    React.ElementRef<typeof RadioGroupPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> & {
        direction?: VariantProps<typeof radioGroupVariants>['direction'];
        size?: VariantProps<typeof radioItemVariants>['size'];

        bordered?: boolean;
        block?: boolean;

        hasError?: boolean;
    }
>(({ direction, className, size, hasError, bordered, block, ...props }, ref) => {
    const ctxVal = useMemo(() => ({ direction, size, block }), [direction, size, block]);

    return (
        <RadioCTX.Provider value={ctxVal}>
            <RadioGroupPrimitive.Root
                className={cn(
                    radioGroupVariants({
                        direction: direction,
                        hasError: hasError,
                        bordered: bordered ?? false,
                        block: block ?? false,
                    }),
                    className
                )}
                {...props}
                ref={ref}
            />
        </RadioCTX.Provider>
    );
});
RadioButtonGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioCTX = createContext<{
    direction: VariantProps<typeof radioGroupVariants>['direction'];
    size: VariantProps<typeof radioItemVariants>['size'];
    block: VariantProps<typeof radioGroupVariants>['block'];
}>({
    direction: 'horizontal',
    size: 'sm',
    block: false,
});

const useParentParams = () => {
    return useContext(RadioCTX);
};

const RadioButtonGroupItem = React.forwardRef<
    React.ElementRef<typeof RadioGroupPrimitive.Item>,
    Omit<React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>, 'prefix'> & {
        indicatorClassName?: string;
        prefix?: React.ReactNode;
        block?: boolean;
    }
>(({ indicatorClassName, className, prefix, block, ...props }, ref) => {
    const { direction: parentDirection, size: parentSize, block: parentBlock } = useParentParams();

    return (
        <RadioGroupPrimitive.Item
            className={cn(
                radioItemVariants({
                    direction: parentDirection,
                    size: parentSize,
                    block: typeof block === 'boolean' ? block : parentBlock,
                }),
                className
            )}
            ref={ref}
            {...props}
        >
            {prefix ? (
                <span
                    className={buttonTextVariants({
                        disabled: props.disabled,
                        className: 'flex items-center justify-center',
                    })}
                >
                    {prefix}
                </span>
            ) : null}
            {props.children}
        </RadioGroupPrimitive.Item>
    );
});
RadioButtonGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioButtonGroup, RadioButtonGroupItem };
